<template>
    <component :is="layoutComponent">
        <div class="homepage-container">
            <!-- Time-based greeting -->
            <div class="p-3 ms-2">
                <span class="fs-3 text-dark">
                    {{ getTimeBasedGreeting() }}<span v-if="$auth.auth && userFirstName">, {{ userFirstName }}</span> 👋
                </span>
            </div>

            <template v-if="currentBrand?.slug=='siop-academy'">
                <SiopAcademyHomepage />
            </template>
            <template v-else-if="currentBrand?.slug=='ida'">
                <IdaAcademyHomepage />
            </template>
            <template v-else-if="currentBrand?.slug=='act4c'">
                <Act4cHomepage />
            </template>
            <template v-else>

            <!-- Dashboard Cards -->
            <div class="container-fluid px-3">
                <div class="row g-3">
                    <!-- Your Networks Card -->
                    <div v-if="this.$auth.auth" class="col-lg-6 col-md-6 col-sm-12">
                        <div class="card card-hover h-100 shadow-sm">
                            <div class="card-body">
                                <div class="d-flex justify-content-between align-items-center mb-3">
                                    <h5 class="card-title text-start mb-0">{{ $t('your-networks') }}</h5>
                                </div>
                                <div class="card-content">
                                    <div v-if="orderedDisplayNetworks.length > 0" class="networks-table">
                                        <div
                                            v-for="(network, index) in orderedDisplayNetworks"
                                            :key="network.id || index"
                                            class="network-row d-flex align-items-center py-2 border-bottom"
                                        >
                                            <div class="col-6 pe-2">
                                                <div class="network-name text-truncate">
                                                    <router-link :to="`/networks/${network.slug}`" class="text-decoration-none text-dark">
                                                        {{ network.name }}
                                                    </router-link>
                                                </div>
                                            </div>
                                            <div class="col-3 px-2">
                                                <div class="network-stats text-secondary">
                                                    <div>{{ network.num_users || 0 }} {{ $t('members') }}</div>
                                                    <div>{{ network.media_count || 0 }} {{ $t('media') }}</div>
                                                </div>
                                            </div>
                                            <div class="col-3 ps-2">
                                                <div v-if="network.moderation && canModerateNetwork(network)" class="pending-requests">
                                                    <span v-if="network.pending_requests_count > 0" class="text-dark small">
                                                        {{ network.pending_requests_count }} {{ $t('pending-requests') }}
                                                    </span>
                                                    <span v-else-if="loadingPendingRequests" class="text-muted small">
                                                        <i class="fas fa-spinner fa-spin"></i>
                                                    </span>
                                                </div>
                                                <div v-else class="notifications-count">
                                                    <span v-if="getNetworkNotificationCount(network.slug) > 0" class="text-primary small">
                                                        {{ getNetworkNotificationCount(network.slug) }} {{ $t('new-notifications') }}
                                                    </span>
                                                    <span v-else-if="loadingNotifications" class="text-muted small">
                                                        <i class="fas fa-spinner fa-spin"></i>
                                                    </span>
                                                    <span v-else class="text-muted small">
                                                        {{ $t('no-new-notifications') }}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else-if="!myManagedNetworks && !myNetworks" class="text-muted text-center py-4">
                                        {{ $t('loading-your-networks') }}
                                    </div>
                                    <div v-else class="text-muted text-center py-4">
                                        {{ $t('you-havent-joined-any-networks-yet') }}
                                    </div>
                                </div>
                                <div class="d-flex justify-content-between align-items-center pt-3 mb-n2">
                                    <div v-if="hasMoreThanFiveNetworks" class="text-muted small" style="font-size: 0.8rem;">
                                        {{ $t('showing-five-of-networks', { num: totalUserNetworksCount }) }}
                                    </div>
                                    <div v-else></div>
                                    <router-link :to="getNetworksRouteForYourNetworks()" class="btn btn-outline-primary btn-sm custom-border fw-semibold">
                                        {{ $t('network.viewAll') }}
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Media You Would Like Card -->
                    <div class="col-lg-6 col-md-6 col-sm-12">
                        <div class="card card-hover h-100 shadow-sm">
                            <div class="card-body pb-0">
                                <h5 class="card-title text-start mb-2">{{ $t('media-you-would-like') }}</h5>
                                <div class="card-content">
                                    <div v-if="recommendedMedia.length > 0">
                                        <div id="mediaCarousel" class="carousel slide" data-bs-ride="carousel">
                                            <div class="carousel-inner">
                                                <div
                                                    v-for="(media, index) in recommendedMedia"
                                                    :key="index"
                                                    :class="['carousel-item', { active: index === currentSlideIndex }]"
                                                >
                                                    <div class="carousel-content d-flex p-3">
                                                        <div class="media-thumbnail-container">
                                                            <div class="media-thumbnail-frame">
                                                                <div class="media-carousel-thumbnail">
                                                                    <router-link v-if="media.thumbnail_large" :to="`/media/${media.slug}`">
                                                                        <img
                                                                            :src="media.thumbnail_large"
                                                                            :alt="media.title"
                                                                        />
                                                                    </router-link>
                                                                    <div v-else class="thumbnail-placeholder rounded d-flex align-items-center justify-content-center">
                                                                        <i class="fa-solid fa-circle-play text-primary fs-1"></i>
                                                                    </div>
                                                                </div>
                                                                <span v-if="media.reason" class="badge media-reason-badge">
                                                                    {{ media.reason }}
                                                                </span>
                                                            </div>
                                                        </div>
                                                        <div class="media-content-container">
                                                            <div class="media-carousel-content">
                                                                <router-link :to="`/media/${media.slug}`" class="fs-6 media-carousel-title fw-semibold mb-2 d-block">
                                                                    {{ media.title }}
                                                                </router-link>
                                                                <div v-if="media.description" class="media-carousel-description quill-content" v-html="media.description">
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <!-- Fixed carousel controls outside of carousel items -->
                                            <div class="carousel-controls-container d-flex">
                                                <div class="carousel-navigation d-flex align-items-center">
                                                    <button class="carousel-control-prev me-1" type="button" data-bs-target="#mediaCarousel" data-bs-slide="prev">
                                                        <span class="carousel-control-prev-icon" aria-hidden="true"></span>
                                                        <span class="visually-hidden">{{ $t('previous') }}</span>
                                                    </button>
                                                    <button
                                                        v-for="(media, index) in recommendedMedia"
                                                        :key="index"
                                                        type="button"
                                                        data-bs-target="#mediaCarousel"
                                                        :data-bs-slide-to="index"
                                                        :class="{ active: index === currentSlideIndex }"
                                                        class="carousel-indicator-btn"
                                                    ></button>
                                                    <button class="carousel-control-next" type="button" data-bs-target="#mediaCarousel" data-bs-slide="next">
                                                        <span class="carousel-control-next-icon" aria-hidden="true"></span>
                                                        <span class="visually-hidden">{{ $t('next') }}</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else-if="loadingMedia" class="text-muted text-center py-4">
                                        {{ $t('loading-recommended-media') }}
                                    </div>
                                    <div v-else class="text-muted text-center py-4">
                                        {{ $t('no-recommended-media-available') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Network Recommendations Card -->
                    <div class="col-lg-6 col-md-6 col-sm-12">
                        <div class="card card-hover h-100 shadow-sm">
                            <div class="card-body">
                                <div class="d-flex justify-content-between align-items-center mb-3">
                                    <h5 class="card-title text-start mb-0">{{ $t('network-recommendations') }}</h5>
                                </div>
                                <div class="card-content">
                                    <div v-if="shuffledRecommendedNetworks.length > 0" class="networks-table">
                                        <div
                                            v-for="(network, index) in shuffledRecommendedNetworks"
                                            :key="network.id || index"
                                            class="network-row d-flex align-items-center py-2 border-bottom"
                                        >
                                            <div class="col-6 pe-2">
                                                <div class="network-name text-truncate">
                                                    <router-link :to="`/networks/${network.slug}`" class="text-decoration-none text-dark">
                                                        {{ network.name }}
                                                    </router-link>
                                                </div>
                                            </div>
                                            <div class="col-3 px-2">
                                                <div class="network-stats text-secondary">
                                                    <div>{{ network.num_users || 0 }} {{ $t('members') }}</div>
                                                    <div>{{ network.media_count || 0 }} {{ $t('media') }}</div>
                                                </div>
                                            </div>
                                            <div class="col-3 ps-2">
                                                <div class="recommendation-reason fw-semibold">
                                                    {{ network.reason }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else-if="!discoverNetworks" class="text-muted text-center py-4">
                                        {{ $t('loading-network-recommendations') }}
                                    </div>
                                    <div v-else class="text-muted text-center py-4">
                                        {{ $t('no-network-recommendations-available') }}
                                    </div>
                                </div>
                                <div class="text-end pt-3 mb-n2">
                                    <router-link to="/networks?view=discover" class="btn btn-outline-primary btn-sm custom-border fw-semibold">
                                        {{ $t('discover-more') }}
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Upcoming Events Card -->
                    <div class="col-lg-6 col-md-6 col-sm-12">
                        <div class="card card-hover h-100 shadow-sm">
                            <div class="card-body">
                                <h5 class="card-title text-start mb-3">{{ $t('upcoming-events') }}</h5>
                                <div class="card-content">
                                    <div v-if="upcomingEvents.length > 0" class="events-table">
                                        <div
                                            v-for="(event, index) in upcomingEvents"
                                            :key="index"
                                            class="event-row d-flex align-items-start border-bottom"
                                        >
                                            <div class="col-3 pe-3">
                                                <div class="event-date small">
                                                    {{ formatEventDate(event.start) }}
                                                </div>
                                            </div>
                                            <div class="col-9 ps-2">
                                                <div class="event-title text-truncate fw-semibold mb-1">
                                                    <router-link :to="event.link" class="text-decoration-none text-dark">
                                                        {{ event.title }}
                                                    </router-link>
                                                </div>
                                                <div class="event-meta small">
                                                    {{ formatPresenter(event.presenter) }} | {{ event.network_name }}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else-if="loadingEvents" class="text-muted text-center py-4">
                                        {{ $t('loading-upcoming-events') }}
                                    </div>
                                    <div v-else class="text-muted text-center py-4">
                                        {{ $t('no-upcoming-events-found') }}
                                    </div>
                                </div>
                                <div class="text-end pt-3 mb-n2">
                                    <router-link to="/events" class="btn btn-outline-primary btn-sm custom-border fw-semibold">
                                        {{ $t('network.viewMore') }}
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                v-if="showUserDataModal"
                :show.sync="showUserDataModal"
                :closeOnEsc="true"
                :closeOnBackdrop="true"
                :showClose="false"
                size="md"
            >
                <div class="card card-hover shadow-lg p-0" style="margin: -24px -21px;">
                    <div class="card-body p-0">
                        <div class="col-12 px-3 pt-3">
                            <div class="progress" style="height: 24px; border-radius: 0.75rem; position: relative;">
                                <div
                                    class="progress-bar d-flex align-items-center justify-content-center"
                                    role="progressbar"
                                    :style="{ width: `${Math.max(0, Math.min(100, Number(userData.progress || 0)))}%`, backgroundColor: '#005F9A' }"
                                    :aria-valuenow="Math.max(0, Math.min(100, Number(userData.progress || 0)))"
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                >
                                    <small class="text-white fw-bold">{{ Math.round(Math.max(0, Math.min(100, Number(userData.progress || 0)))) }}%</small>
                                </div>
                            </div>
                        </div>
                        <div class="p-2 mb-0"></div>
                        <div class="p-4 d-flex justify-content-between" style="background-color: #E5F6F9;">
                            <span class="mx-3 mt-2 fs-5">
                                {{ $t('to-help-us-get-to-know-you-better-please-finish-setting-up-your-account') }}
                            </span>
                        </div>
                    </div>
                    <div class="card-footer border-0 text-start p-3">
                        <button
                            class="btn btn-primary me-4 mb-1"
                            data-test="complete_profile"
                            style="height: 40px; padding: 0.35rem 1.3rem !important;"
                            @click="onCompleteProfileClick"
                        >
                            <span class="align-middle">
                                {{ $t('complete-your-account-setup') }}
                            </span>
                        </button>
                        <button data-test="skip_for_now" class="btn btn-outline-primary mb-1" style="height: 40px; border: 2px solid; padding: 0.35rem 1.3rem !important;" @click="skipForNow">
                            <span class="align-middle">
                                {{ $t('skip-for-now') }}
                            </span>
                        </button>
                    </div>
                </div>
            </Modal>
            </template>
        </div>
    </component>
</template>
<script>
import DefaultLayout from './layouts/default/';
import SiopAcademyHomepage from './homepage-components/SiopAcademyHomepage.vue';
import IdaAcademyHomepage from './homepage-components/IdaAcademyHomepage.vue';
import Act4cHomepage from './homepage-components/Act4cHomepage.vue';
import Modal from '@/components/commons/Modals/Modal.vue';
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import bootstrap from 'bootstrap/dist/js/bootstrap.bundle.min.js';

export default {
    name: 'Homepage',
    components: {
        DefaultLayout,
        SiopAcademyHomepage,
        IdaAcademyHomepage,
        Act4cHomepage,
        Modal,
    },
    data() {
        return {
            events: [],
            loadingEvents: false,
            recommendedMedia: [],
            loadingMedia: false,
            currentSlideIndex: 0,
            orderedDisplayNetworks: [],
            shuffledRecommendedNetworks: [],
            loadingPendingRequests: false,
            loadingNotifications: true,
            showUserDataModal: false,
            userDataLoaded: false,
            carouselSlidRef: null,
            loadingUserData: false,
        };
    },
    computed: {
        ...mapState('network', ['myManagedNetworks', 'myNetworks', 'trendingNetworks', 'userData', 'discoverNetworks']),
        ...mapGetters(['currentBrand']),
        layoutComponent() {
            return 'DefaultLayout';
        },
        networkNotificationCounts() {
            return this.$store.getters['notifications/getNetworkNotificationCounts'] || {};
        },
        userFirstName() {
            return this.userData?.general?.first_name ?? this.$auth?.user?.first_name ?? null;
        },
        shouldShowModal() {
            // don't show modal if user is not authenticated
            if (!this.$auth.auth) {
                return false;
            }

            // wait for userData to be loaded to avoid race conditions during impersonation
            if (!this.userData || !this.userDataLoaded) {
                return false;
            }

            // don't show modal if user progress is 99% or higher AND requirements are complete
            const userProgress = Number(this.userData.progress || 0);
            if (userProgress >= 99 && this.userData.are_requirements_complete === true) {
                return false;
            }

            // check localStorage skip timestamp
            let skipUntil = null;
            try {
                skipUntil = localStorage.getItem('userDataModalSkipUntil');
            } catch (_) {
                // storage unavailable; fail open (show modal)
                return true;
            }
            if (skipUntil) {
                const parsedTimestamp = Number.parseInt(skipUntil, 10);
                if (!Number.isFinite(parsedTimestamp)) {
                    // invalid timestamp; clear and fail open (show modal)
                    try { localStorage.removeItem('userDataModalSkipUntil'); } catch (_) {}
                    return true;
                }
                return Date.now() > parsedTimestamp;
            }
            return true;
        },
        displayNetworks() {
            const managedNetworks = this.myManagedNetworks || [];
            const regularNetworks = this.myNetworks || [];

            // sort managed networks by member count (descending)
            const sortedManagedNetworks = [...managedNetworks]
                .sort((a, b) => (b.num_users || 0) - (a.num_users || 0));

            // filter out managed networks from regular networks to avoid duplicates
            const availableRegularNetworks = regularNetworks.filter(
                network => !managedNetworks.some(managed => managed.id === network.id)
            );

            // sort regular networks by member count (descending)
            const sortedRegularNetworks = [...availableRegularNetworks]
                .sort((a, b) => (b.num_users || 0) - (a.num_users || 0));

            // combine: managed networks first, then joined networks
            let selectedNetworks = [...sortedManagedNetworks];

            // if we need more networks, add from sorted regular networks
            if (selectedNetworks.length < 5) {
                const needed = 5 - selectedNetworks.length;
                selectedNetworks = [...selectedNetworks, ...sortedRegularNetworks.slice(0, needed)];
            }

            // return up to 5 networks in fixed order (no shuffling)
            return selectedNetworks.slice(0, 5);
        },
        totalUserNetworksCount() {
            const managedNetworks = this.myManagedNetworks || [];
            const regularNetworks = this.myNetworks || [];

            // count unique networks (managed + joined, excluding duplicates)
            const allNetworkIds = new Set();
            managedNetworks.forEach(network => allNetworkIds.add(network.id));
            regularNetworks.forEach(network => allNetworkIds.add(network.id));

            return allNetworkIds.size;
        },
        hasMoreThanFiveNetworks() {
            return this.totalUserNetworksCount > 5;
        },
        upcomingEvents() {
            // only process if events array exists and has data
            if (!this.events || this.events.length === 0) {
                return [];
            }

            // sort by date ascending (soonest first) and take the first 5
            // create a copy to avoid mutating the original array
            const sortedEvents = [...this.events]
                .sort((a, b) => new Date(a.start) - new Date(b.start))
                .slice(0, 5);

            return sortedEvents;
        },
        recommendedNetworks() {
            if (!this.discoverNetworks || this.discoverNetworks.length === 0) {
                return [];
            }

            const recommendations = [];
            const userNetworks = [...(this.myManagedNetworks || []), ...(this.myNetworks || [])];
            const trendingNetworks = this.trendingNetworks || [];

            // process each discover network and add recommendation reasons
            this.discoverNetworks.forEach(network => {
                const reasons = [];

                // check if it's a trending network
                if (trendingNetworks.some(trending => trending.id === network.id)) {
                    reasons.push(this.$t('network.trending'));
                }

                // check for similar networks (if user is logged in)
                if (this.$auth.auth && userNetworks.length > 0) {
                    // define words to exclude from comparison
                    const excludeWords = new Set(['and', 'or', 'the', 'of', 'a', 'an', 'network', 'networks', 'group', 'groups', 'community', 'communities']);

                    const networkWords = (network.name || '').toLowerCase()
                        .split(/[\s\-_]+/)
                        .filter(word => word.length > 3 && !excludeWords.has(word));

                    const hasMatch = userNetworks.some(userNetwork => {
                        const userWords = (userNetwork.name || '').toLowerCase()
                            .split(/[\s\-_]+/)
                            .filter(word => word.length > 3 && !excludeWords.has(word));

                        return networkWords.some(word =>
                            userWords.some(userWord =>
                                userWord.includes(word) || word.includes(userWord)
                            )
                        );
                    });
                    if (hasMatch) {
                        reasons.push(this.$t('similar-interests'));
                    }
                }

                // check member count for popular networks
                if ((network.num_users || 0) > 100) {
                    reasons.push(this.$t('popular'));
                }

                // check for active networks (if they have recent activity/media)
                if ((network.media_count || 0) > 25) {
                    reasons.push(this.$t('active-community'));
                }

                // check if it's a new network (created in the last month)
                if (network.created_at) {
                    const oneMonthAgo = new Date();
                    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
                    const createdDate = new Date(network.created_at);
                    if (createdDate > oneMonthAgo) {
                        reasons.push(this.$t('new-network'));
                    }
                }

                // default reason if no specific reason found
                if (reasons.length === 0) {
                    reasons.push(this.$t('explore'));
                }

                recommendations.push({
                    ...network,
                    reason: reasons[0] // use the first/most relevant reason
                });
            });

            // return up to 5 recommendations without shuffling (shuffling is done separately)
            return recommendations;
        },
    },
    watch: {
        displayNetworks: {
            handler(newNetworks, oldNetworks) {
                // only update and load pending requests if the networks actually changed
                if (this.hasNetworksChanged(newNetworks, oldNetworks)) {
                    // create shallow copies of networks to avoid mutating store state
                    const networkCopies = newNetworks.map(network => ({ ...network }));
                    this.orderedDisplayNetworks = networkCopies;
                    this.loadPendingRequestsForNetworks(this.orderedDisplayNetworks);
                }
            },
            deep: true,
            immediate: true
        },
        recommendedNetworks: {
            handler(newNetworks, oldNetworks) {
                // only shuffle and load media if the networks actually changed
                if (this.hasNetworksChanged(newNetworks, oldNetworks)) {
                    this.shuffledRecommendedNetworks = this.shuffleArray([...newNetworks]).slice(0, 5);
                }
            },
            deep: true,
            immediate: true
        },
        shouldShowModal(newVal) {
            this.showUserDataModal = !!newVal;
        },
        userData: {
            handler(newUserData) {
                // mark userData as loaded when we receive it
                if (newUserData) {
                    this.userDataLoaded = true;
                }
            },
            deep: true
        },
        '$auth.auth': {
            handler(newAuth, oldAuth) {
                // reset loading state when auth changes (for impersonation)
                if (newAuth !== oldAuth) {
                    this.userDataLoaded = false;
                    this.showUserDataModal = false;

                    // reset loading flag on auth change
                    this.loadingUserData = false;
                }
            }
        },
        '$auth.user': {
            handler(newUser, oldUser) {
                // check for user changes using multiple possible identifiers
                let userChanged = false;
                let authStateChanged = false;

                // detect authentication state changes (login/logout)
                if ((!oldUser && newUser) || (oldUser && !newUser)) {
                    authStateChanged = true;
                }

                if (newUser && oldUser) {
                    // check ID at different possible locations
                    if (newUser.user_id !== oldUser.user_id) {
                        userChanged = true;
                    } else if (newUser.first_name !== oldUser.first_name) {
                        userChanged = true;
                    } else if (newUser.email !== oldUser.email) {
                        userChanged = true;
                    }
                }

                // handle user changes (impersonation scenarios) or auth state changes
                if (userChanged || authStateChanged) {
                    this.userDataLoaded = false;
                    this.showUserDataModal = false;

                    // load user data if authenticated and not already loading
                    if (newUser && this.$auth.auth && !this.loadingUserData) {
                        this.loadingUserData = true;
                        this.$nextTick(() => {
                            this.$store.dispatch('network/loadUserData', { search: '' })
                                .finally(() => {
                                    this.loadingUserData = false;
                                });
                        });
                    }
                }
            },
            deep: true
        },
    },
    beforeDestroy() {
        const carouselElement = document.getElementById('mediaCarousel');
        if (carouselElement && this.carouselSlidRef) {
            carouselElement.removeEventListener('slid.bs.carousel', this.carouselSlidRef);
            this.carouselSlidRef = null;
        }
    },
    mounted() {
        if (this.$route.query.v3_denied) {
            this.$store.dispatch('toast/showToast', {
                message: "You don't have access to the new homepage yet.",
                variant: 'warning',
                autohide: true,
            });
            const query = { ...this.$route.query };
            delete query.v3_denied;
            this.$router.replace({ path: this.$route.path, query });
        }
        if (this.$auth.auth && this.$auth.user && !this.loadingUserData) {
            this.loadingUserData = true;
            this.$store.dispatch('network/loadUserData', { search: '' })
                .finally(() => {
                    this.loadingUserData = false;
                });
            // load network notification counts
            this.loadNotificationCounts();
        }
        // load upcoming events
        this.loadUpcomingEvents();
        // load recommended media immediately
        this.loadRecommendedMedia();
        // derived by shouldShowModal; initialize closed
        this.showUserDataModal = false;
    },
    methods: {
        getTimeBasedGreeting() {
            const hour = new Date().getHours();
            if (hour >= 5 && hour < 12) {
                return this.$t('good-morning');
            } else if (hour >= 12 && hour < 18) {
                return this.$t('good-afternoon');
            } else {
                return this.$t('good-evening');
            }
        },
        getNetworkNotificationCount(networkSlug) {
            const counts = this.networkNotificationCounts;
            if (counts && counts[networkSlug]) {
                return counts[networkSlug];
            }
            return 0;
        },
        async loadNotificationCounts() {
            this.loadingNotifications = true;
            try {
                await this.$store.dispatch('notifications/fetchNetworkNotificationCounts');
            } catch (error) {
                console.error('Error loading notification counts:', error);
            } finally {
                this.loadingNotifications = false;
            }
        },
        canModerateNetwork(network) {
            if ((this.$auth.auth && this.$auth.hasRole('super-admin')) ||
                network.my_role === 'admin' ||
                network.is_moderator === true) {
                return true;
            }

            // fallback: check if they're in myManagedNetworks
            if (this.myManagedNetworks) {
                const managedNetwork = this.myManagedNetworks.find(mn => mn.id === network.id);
                if (managedNetwork) {
                    return true;
                }
            }
            return false;
        },
        getNetworksRouteForYourNetworks() {
            // if user has any networks (managed or joined), go to yours view
            if ((this.myManagedNetworks && this.myManagedNetworks.length > 0) ||
                (this.myNetworks && this.myNetworks.length > 0)) {
                return '/networks?view=yours';
            }
            // fallback to discover view
            return '/networks?view=discover';
        },
        shuffleArray(array) {
            const shuffled = [...array];
            for (let i = shuffled.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            return shuffled;
        },
        async loadUpcomingEvents() {
            this.loadingEvents = true;
            try {
                const response = await this.$http.get('/networks/events');

                if (response.data && response.data.upcomingWithOccurrences) {
                    const processedEvents = [];
                    const processedMeetings = {};
                    const now = new Date();

                    response.data.upcomingWithOccurrences.forEach(meeting => {
                        if (meeting.occurrences && meeting.occurrences.length > 0) {
                            meeting.occurrences.forEach(occurrence => {
                                const occurrenceKey = `${meeting.id}-${occurrence.occurrence_id}`;
                                if (!processedMeetings[occurrenceKey]) {
                                    const meetingDate = new Date(occurrence.start);
                                    if (meetingDate > now) {
                                        // use presenter data from occurrence if available, otherwise from meeting
                                        let presenterData = [];
                                        if (occurrence.presenter && Array.isArray(occurrence.presenter) && occurrence.presenter.length > 0) {
                                            presenterData = occurrence.presenter;
                                        } else if (meeting.presenter && Array.isArray(meeting.presenter) && meeting.presenter.length > 0) {
                                            presenterData = meeting.presenter;
                                        }
                                        const eventData = {
                                            id: meeting.id,
                                            occurrence_id: occurrence.occurrence_id,
                                            title: occurrence.title || meeting.title,
                                            content: occurrence.content || meeting.content,
                                            start: occurrence.start,
                                            duration: occurrence.duration || meeting.duration,
                                            network_id: meeting.network.id,
                                            network_short_name: meeting.network.short_name,
                                            network_name: meeting.network.name,
                                            network_slug: meeting.network.slug,
                                            presenter: presenterData,
                                            link: occurrence.link || `/networks/${meeting.network.slug}/meetings/${meeting.id}?occurrence=${occurrence.occurrence_id}`
                                        };
                                        processedEvents.push(eventData);
                                    }
                                    processedMeetings[occurrenceKey] = true;
                                }
                            });
                        } else {
                            // process main meetings without occurrences
                            const meetingKey = `${meeting.id}-main`;
                            if (!processedMeetings[meetingKey]) {
                                const meetingDate = new Date(meeting.start);
                                if (meetingDate > now) {
                                    let presenterData = [];
                                    if (meeting.presenter && Array.isArray(meeting.presenter) && meeting.presenter.length > 0) {
                                        presenterData = meeting.presenter;
                                    }
                                    const eventData = {
                                        id: meeting.id,
                                        occurrence_id: null,
                                        title: meeting.title,
                                        content: meeting.content,
                                        start: meeting.start,
                                        duration: meeting.duration,
                                        network_id: meeting.network.id,
                                        network_short_name: meeting.network.short_name,
                                        network_name: meeting.network.name,
                                        network_slug: meeting.network.slug,
                                        presenter: presenterData,
                                        link: meeting.link || `/networks/${meeting.network.slug}/meetings/${meeting.id}`
                                    };
                                    processedEvents.push(eventData);
                                }
                                processedMeetings[meetingKey] = true;
                            }
                        }
                    });
                    this.$set(this, 'events', processedEvents);
                } else {
                    this.$set(this, 'events', []);
                }
            } catch (error) {
                console.error('Error loading events:', error);
                this.$set(this, 'events', []);
            } finally {
                this.loadingEvents = false;
            }
        },
        formatEventDate(date) {
            return moment(date).format('MMM D, YYYY • h:mm A');
        },
        formatPresenter(presenter) {
            if (!presenter || !Array.isArray(presenter) || presenter.length === 0) {
                return 'N/A';
            }
            return presenter.map(p => p.name || this.$t('unknown')).join(', ');
        },
        isUnwatchedVideo(media) {
            return media && media.type === 'video' && !media.has_accessed;
        },
        async loadRecommendedMedia() {
            if (this.loadingMedia) {
                return;
            }
            this.loadingMedia = true;

            try {
                let allMedia = [];

                // function to get media from a batch of networks and filter by access
                const getMediaFromNetworks = async (networks) => {
                    if (!networks || networks.length === 0) {
                        return [];
                    }

                    const networkIds = networks.filter(network => network && network.id).map(network => network.id).join(',');
                    if (!networkIds) return [];

                    try {
                        const response = await this.$http.get('/networks/feed', {
                            params: {
                                network_id: networkIds,
                                media_type: ['video'],
                                limit: 50, // increased to account for filtering
                                offset: 0
                            }
                        });

                        if (response.data && response.data.results) {
                            // filter for unwatched videos only if user is authenticated
                            if (this.$auth.auth) {
                                return response.data.results.filter(this.isUnwatchedVideo);
                            } else {
                                // for logged-out users, show all videos
                                return response.data.results.filter(media => media && media.type === 'video');
                            }
                        }
                        return [];
                    } catch (error) {
                        console.warn('Failed to fetch media from networks:', error);
                        return [];
                    }
                };

                // function to get public/trending media for logged-out users
                const getPublicMedia = async () => {
                    try {
                        // try to get media from a general feed endpoint without network filtering
                        const response = await this.$http.get('/networks/feed', {
                            params: {
                                media_type: ['video'],
                                limit: 10,
                                offset: 0,
                                public: true // indicate we want public media
                            }
                        });

                        if (response.data && response.data.results) {
                            return response.data.results.filter(media => media && media.type === 'video');
                        }
                        return [];
                    } catch (error) {
                        console.warn('Failed to fetch public media:', error);
                        // fallback: try to get trending networks first, then their media
                        try {
                            const networksResponse = await this.$http.get('/networks/trending_networks');
                            if (networksResponse.data && networksResponse.data.length > 0) {
                                const trendingNetworks = networksResponse.data.slice(0, 10);
                                return await getMediaFromNetworks(trendingNetworks);
                            }
                        } catch (networkError) {
                            console.warn('Failed to fetch trending networks:', networkError);
                        }
                        return [];
                    }
                };

                // handle logged-out users differently
                if (!this.$auth.auth) {
                    // for logged-out users, get public/trending media
                    const publicMedia = await getPublicMedia();
                    if (publicMedia.length > 0) {
                        const publicMediaWithReason = publicMedia.map(media => ({ ...media, reason: this.$t('network.trending') }));
                        allMedia = publicMediaWithReason.slice(0, 5);
                    }
                } else {
                    // existing logic for authenticated users
                    // get similar interest networks first (preferred)
                    let similarNetworks = [];
                    if (this.shuffledRecommendedNetworks && this.shuffledRecommendedNetworks.length > 0) {
                        similarNetworks = this.shuffledRecommendedNetworks.filter(network => network.reason === this.$t('similar-interests'));
                    } else if (this.recommendedNetworks && this.recommendedNetworks.length > 0) {
                        similarNetworks = this.recommendedNetworks.filter(network => network.reason === this.$t('similar-interests'));
                    }

                    // get user networks
                    let userNetworks = [];
                    if (this.myNetworks && this.myNetworks.length > 0) {
                        userNetworks = [...this.myNetworks];
                    }
                    if (this.myManagedNetworks && this.myManagedNetworks.length > 0) {
                        userNetworks = [...userNetworks, ...this.myManagedNetworks];
                    }
                    // remove duplicates from user networks
                    const uniqueUserNetworks = userNetworks.filter((network, index, self) =>
                        index === self.findIndex(n => n.id === network.id)
                    );

                    // try similar networks first (limit to 3 items to reserve space for user networks)
                    if (similarNetworks.length > 0) {
                        const shuffledSimilar = this.shuffleArray([...similarNetworks]);
                        const similarMedia = await getMediaFromNetworks(shuffledSimilar);
                        const similarMediaWithReason = similarMedia.map(media => ({ ...media, reason: this.$t('similar-interests') })).slice(0, 3);
                        allMedia = [...allMedia, ...similarMediaWithReason];
                    }

                    // try user networks (aim for 2 items, exclude those already used in similar)
                    if (uniqueUserNetworks.length > 0) {
                        const availableUserNetworks = uniqueUserNetworks.filter(
                            network => !similarNetworks.some(sn => sn.id === network.id)
                        );

                        if (availableUserNetworks.length > 0) {
                            const shuffledUserNetworks = this.shuffleArray(availableUserNetworks);
                            const userMedia = await getMediaFromNetworks(shuffledUserNetworks);
                            const userMediaWithReason = userMedia.map(media => ({ ...media, reason: this.$t('from-my-networks') })).slice(0, 2);
                            allMedia = [...allMedia, ...userMediaWithReason];
                        }
                    }
                }

                // aggressive fallback to ensure we get 5 items
                if (allMedia.length < 5) {
                    let fallbackNetworks = [];
                    const usedNetworkIds = new Set();

                    // collect already used networks (only for authenticated users)
                    if (this.$auth.auth) {
                        const similarNetworks = this.shuffledRecommendedNetworks?.filter(network => network.reason === this.$t('similar-interests')) || [];
                        let userNetworks = [];
                        if (this.myNetworks && this.myNetworks.length > 0) {
                            userNetworks = [...this.myNetworks];
                        }
                        if (this.myManagedNetworks && this.myManagedNetworks.length > 0) {
                            userNetworks = [...userNetworks, ...this.myManagedNetworks];
                        }
                        const uniqueUserNetworks = userNetworks.filter((network, index, self) =>
                            index === self.findIndex(n => n.id === network.id)
                        );

                        [...similarNetworks, ...uniqueUserNetworks].forEach(network => {
                            if (network && network.id) usedNetworkIds.add(network.id);
                        });
                    }

                    // try trending networks first
                    if (this.trendingNetworks && this.trendingNetworks.length > 0) {
                        const unusedTrending = this.trendingNetworks.filter(network => !usedNetworkIds.has(network.id));
                        fallbackNetworks = [...fallbackNetworks, ...unusedTrending];
                    }

                    // try discover networks
                    if (this.discoverNetworks && this.discoverNetworks.length > 0) {
                        const unusedDiscover = this.discoverNetworks.filter(network => !usedNetworkIds.has(network.id));
                        fallbackNetworks = [...fallbackNetworks, ...unusedDiscover];
                    }

                    // if still not enough networks, try recommended networks
                    if (fallbackNetworks.length === 0 && this.shuffledRecommendedNetworks && this.shuffledRecommendedNetworks.length > 0) {
                        const unusedRecommended = this.shuffledRecommendedNetworks.filter(network => !usedNetworkIds.has(network.id));
                        fallbackNetworks = [...fallbackNetworks, ...unusedRecommended];
                    }

                    // last resort: fetch from API
                    if (fallbackNetworks.length === 0) {
                        try {
                            const networksResponse = await this.$http.get('/networks/trending_networks');
                            if (networksResponse.data && networksResponse.data.length > 0) {
                                const unusedApiNetworks = networksResponse.data.filter(network => !usedNetworkIds.has(network.id));
                                fallbackNetworks = [...fallbackNetworks, ...unusedApiNetworks];
                            }
                        } catch (error) {
                            console.warn('Could not fetch trending networks from API:', error);
                        }
                    }

                    if (fallbackNetworks.length > 0) {
                        const shuffledFallback = this.shuffleArray([...fallbackNetworks]);
                        const fallbackMedia = await getMediaFromNetworks(shuffledFallback.slice(0, 15)); // try more networks
                        const fallbackMediaWithReason = fallbackMedia.map(media => ({ ...media, reason: this.$t('network.trending') }));
                        allMedia = [...allMedia, ...fallbackMediaWithReason];
                    }

                    // final fallback for logged-out users if we still have no media
                    if (allMedia.length === 0 && !this.$auth.auth) {
                        const publicMedia = await getPublicMedia();
                        if (publicMedia.length > 0) {
                            const publicMediaWithReason = publicMedia.map(media => ({ ...media, reason: this.$t('explore') }));
                            allMedia = publicMediaWithReason.slice(0, 5);
                        }
                    }
                }

                // final processing - always try to get exactly 5 items
                if (allMedia.length > 0) {
                    // remove duplicates based on id or slug
                    const uniqueMedia = allMedia.filter((media, index, self) =>
                        index === self.findIndex(m => m.id === media.id || m.slug === media.slug)
                    );

                    // if we have fewer than 5 unique items for authenticated users, relax the unwatched filter as last resort
                    let finalMedia = uniqueMedia;
                    if (finalMedia.length < 5 && this.$auth.auth) {
                        // try to get more media without the unwatched filter
                        const getAllMedia = async (networks) => {
                            if (!networks || networks.length === 0) return [];

                            const networkIds = networks.filter(network => network && network.id).map(network => network.id).join(',');
                            if (!networkIds) return [];

                            try {
                                const response = await this.$http.get('/networks/feed', {
                                    params: { network_id: networkIds, media_type: ['video'], limit: 20, offset: 0 }
                                });
                                return response.data?.results || [];
                            } catch (error) {
                                return [];
                            }
                        };

                        // get more media from trending/discover networks without unwatched filter
                        if (this.trendingNetworks && this.trendingNetworks.length > 0) {
                            const additionalMedia = await getAllMedia(this.trendingNetworks.slice(0, 5));
                            const watchedMedia = additionalMedia
                                .filter(media => !finalMedia.some(existing => existing.id === media.id))
                                .map(media => ({ ...media, reason: this.$t('network.trending') }));
                            finalMedia = [...finalMedia, ...watchedMedia];
                        }
                    }

                    // shuffle and take up to 5
                    const shuffledMedia = this.shuffleArray(finalMedia);
                    const selectedMedia = shuffledMedia.slice(0, 5);
                    this.$set(this, 'recommendedMedia', selectedMedia);

                    // initialize carousel if we have media
                    if (selectedMedia.length > 0) {
                        this.$nextTick(() => {
                            if (typeof bootstrap !== 'undefined' && document.getElementById('mediaCarousel')) {
                                const carouselInstance = new bootstrap.Carousel(document.getElementById('mediaCarousel'), {
                                    interval: 5000, // 5 seconds per slide
                                    ride: 'carousel', // enable auto-sliding
                                    wrap: true, // loop through slides
                                    touch: true // allow touch swipe on mobile
                                });
                                this.setupCarouselEventListeners();
                                // start automatic sliding
                                carouselInstance.cycle();
                            }
                        });
                    }
                } else {
                    // if no media found, log warning but don't show critical error for logged-out users
                    if (this.$auth.auth) {
                        console.warn('No recommended media found for authenticated user');
                    } else {
                        console.warn('No public media available for logged-out user');
                    }
                    this.$set(this, 'recommendedMedia', []);
                }
            } catch (error) {
                console.error('❌ Critical error in loadRecommendedMedia:', error);
                this.$set(this, 'recommendedMedia', []);
            } finally {
                this.loadingMedia = false;
            }
        },
        setupCarouselEventListeners() {
            const carouselElement = document.getElementById('mediaCarousel');
            if (carouselElement) {
                // store handler reference for clean-up
                this.carouselSlidRef = (event) => {
                    this.currentSlideIndex = event.to;
                };
                carouselElement.addEventListener('slid.bs.carousel', this.carouselSlidRef);
            }
        },
        hasNetworksChanged(newNetworks, oldNetworks) {
            // if oldNetworks is undefined/null (first run), consider it changed if we have new networks
            if (!oldNetworks) {
                return newNetworks && newNetworks.length > 0;
            }

            // if lengths are different, definitely changed
            if (newNetworks.length !== oldNetworks.length) {
                return true;
            }

            // check if the network IDs are the same
            const newIds = newNetworks.map(n => n.id).sort();
            const oldIds = oldNetworks.map(n => n.id).sort();

            return !newIds.every((id, index) => id === oldIds[index]);
        },
        loadPendingRequestsForNetworks(networks) {
            if (this.loadingPendingRequests) {
                return;
            }

            this.loadingPendingRequests = true;

            const promises = networks
                .filter(network => {
                    return network.moderation && this.canModerateNetwork(network);
                })
                .map(network => this.loadPendingRequestsCount(network));

            Promise.all(promises).finally(() => {
                this.loadingPendingRequests = false;
            });
        },
        async loadPendingRequestsCount(network) {
            try {
                const response = await this.$http.get(`/networks/${network.slug}/moderations`);
                // counts every pending request regardless of email verification -
                // see the matching note in ModerationTab.vue's load()
                const pendingCount = response.data.filter(m =>
                    m.moderation_status === 'Pending'
                ).length;

                // safe to mutate since network is now a copy, not a store reference
                this.$set(network, 'pending_requests_count', pendingCount);
            } catch (error) {
                console.error(`Error loading pending requests for ${network.name}:`, error);
                this.$set(network, 'pending_requests_count', 0);
            }
        },
        cleanupModal() {
            // force cleanup of any modal remnants
            this.$nextTick(() => {
                // remove any modal backdrops
                const backdrops = document.querySelectorAll('.modal-backdrop');
                backdrops.forEach(backdrop => backdrop.remove());

                // remove modal-open class from body
                document.body.classList.remove('modal-open');

                // reset body styles that might be set by modal
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
            });
        },
        onCompleteProfileClick() {
            this.showUserDataModal = false;
            this.cleanupModal();

            this.$nextTick(() => {
                this.$router.push({ path: '/account', query: { tab: 'workinfo' } });
            });
        },
        skipForNow() {
            this.showUserDataModal = false;
            this.cleanupModal();

            this.$nextTick(() => {
                const now = new Date();
                try {
                    const ts = now.getTime() + 5 * 24 * 60 * 60 * 1000; // 5 days
                    localStorage.setItem('userDataModalSkipUntil', ts);
                    this.modalSkipUntil = ts;
                } catch (_) {
                    // noop if storage unavailable
                }
            });
        },
    },
};
</script>
<style scoped>
.homepage-container {
    min-height: 80vh;
    background-color: #f8f9fa;
}
.card {
    border: 1px solid #e9ecef;
    border-radius: 0.5rem;
}
.card:hover {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
}
.card-title {
    color: #000000;
    font-weight: 700;
    font-size: 1.35rem;
}
.card-content {
    min-height: 200px;
}
.networks-table {
    padding: 0;
}
.network-row {
    margin-top: 0.3rem !important;
    margin-bottom: 0.3rem !important;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s ease;
}
.network-row:hover {
    background-color: #f8f9fa;
}
.network-row:last-child {
    border-bottom: none;
}
#mediaCarousel .carousel-content {
    height: 300px;
    width: 100%;
}
.carousel-controls-container {
    position: static;
    z-index: 10;
    margin-bottom: 0;
    margin-top: 7px;
    justify-content: end;
}
.carousel-indicator-btn {
    background-color: rgba(194, 181, 181, 0.5);
    width: 10px;
    height: 10px;
    border-radius: 50%;
    margin-right: 8px;
    margin-top: 0;
    margin-bottom: 0;
    border: none;
    cursor: pointer;
}
.carousel-indicator-btn.active {
    background-color: rgb(180, 171, 171);
}
.carousel-navigation .carousel-control-prev,
.carousel-navigation .carousel-control-next {
    position: static;
    width: 30px;
    height: 30px;
    opacity: 1;
    background-color: rgb(247, 247, 247);
    border-radius: 4px;
    display: flex;
    align-items: center;
}
.carousel-navigation .carousel-control-prev-icon,
.carousel-navigation .carousel-control-next-icon {
    background-size: 70%;
    width: 20px;
    height: 20px;
}
.carousel-navigation .carousel-control-prev-icon {
    background-image: url('/img/icon-prev.svg');
}
.carousel-navigation .carousel-control-next-icon {
    background-image: url('/img/icon-next.svg');
}
.media-thumbnail-container {
    flex: 0 0 55%;
    margin-right: 0.75rem;
}
.media-content-container {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}
.media-thumbnail-frame {
    position: relative;
    width: 100%;
    height: 280px;
    border-radius: 8px;
}
.media-carousel-thumbnail {
    position: absolute;
    inset: 0;
    overflow: hidden;
    border-radius: 8px;
}
.media-carousel-thumbnail img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
}
.media-carousel-thumbnail .thumbnail-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
}
.media-carousel-content {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    padding-top: 0;
}
.media-carousel-title {
    font-size: 1rem;
    line-height: 1.2;
    color: #212529;
    margin-bottom: 0.5rem;
    text-decoration: none;
}
.media-carousel-description {
    font-size: 0.9rem;
    line-height: 1.3;
    color: #6c757d;
    display: -webkit-box;
    -webkit-line-clamp: 7;
    line-clamp: 7;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
/* Quill's editor renders paragraphs with no margin (see QuillEditor.vue); without
   the same reset here, the description falls back to the browser's default <p>
   margin and looks far more spaced out than it did while composing. */
.media-carousel-description ::v-deep p {
    margin: 0;
}
.network-name {
    font-size: 1rem;
    line-height: 1.2;
    font-weight: 600;
}
.network-stats {
    font-size: 0.8rem;
    line-height: 1.3;
}
.recommendation-reason {
    font-size: 0.75rem;
    line-height: 1.2;
}
.text-truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.events-table {
    padding: 0;
}
.event-row {
    margin: 0;
    padding-top: 0.45rem !important;
    padding-bottom: 0.45rem !important;
    border-bottom: 1px solid #f0f0f0;
    transition: background-color 0.2s ease;
}
.event-row:hover {
    background-color: #f8f9fa;
}
.event-row:last-child {
    border-bottom: none;
}
.event-date {
    font-size: 0.85rem;
    color: #6c757d;
}
.event-title {
    font-size: 0.95rem;
    line-height: 1.2;
    color: #212529;
}
.event-meta {
    font-size: 0.8rem;
    line-height: 1.3;
    color: #6c757d;
}
.custom-border {
    border: 3px solid;
}
.media-carousel-thumbnail a {
    display: block;
    line-height: 0;
}
.media-reason-badge {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 10;
    background-color: rgba(0, 95, 154, 0.9);
    color: white;
    font-size: 0.6rem;
    font-weight: 600;
    padding: 0.2rem 0.4rem;
    border-radius: 0.25rem;
    -webkit-backdrop-filter: blur(2px);
    backdrop-filter: blur(2px);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.media-suggestions .bg-light {
    border: 1px solid #f1f3f4;
    transition: all 0.2s ease;
}
.media-suggestions .bg-light:hover {
    background-color: #f8f9fa !important;
    border-color: #dee2e6;
}
.recommendations-list .recommendation-item {
    border: 1px solid #e9ecef;
    transition: all 0.2s ease;
}
.recommendations-list .recommendation-item:hover {
    border-color: #007bff;
    background-color: #f8f9ff;
}
.avatar-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 1.1rem;
}
@media (max-width: 576px) {
    .container-fluid {
        padding-left: 1rem;
        padding-right: 1rem;
    }
    .card-content {
        min-height: 150px;
    }
}
@media (min-width: 992px) {
    .card-content {
        min-height: 250px;
    }
}
</style>
